/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import {
  Component,
  OnInit,
  ViewChild,
  NgZone,
  OnDestroy,
  Input,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { ModalController } from '@ionic/angular';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { CommunicationService } from 'src/app/_services/communication/logic/commumication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { SyncService } from 'src/app/_services/sync/sync.service';
import { LoadingService } from 'src/app/_services/loading/loading.service';
import { Subscription } from 'rxjs';

import { Keyboard } from '@capacitor/keyboard';
import { Browser } from '@capacitor/browser';
import { IMSComponent } from 'src/app/_util/ims-component';
import { Capacitor } from '@capacitor/core';

/**
 * A Blocking Page to force the user to get a valid token id
 *
 * See:
 * https://lbslogics.atlassian.net/wiki/spaces/LBSDEV/pages/838828255/IMS.app+4.0+Token+Generierung
 */
@Component({
  selector: 'app-token-id',
  templateUrl: './token-id.page.html',
  styleUrls: ['./token-id.page.scss'],
})
export class TokenIdPage extends IMSComponent implements OnInit, OnDestroy {
  // Public Member
  public token: string;
  public errorMessage: string;
  public errorDetail: string;
  public statusCode: string;

  // Private Member
  private authSubscription: Subscription;

  // Lifecycle
  constructor(
    translate: TranslateService,
    private authenticationService: AuthenticationService,
    private commService: CommunicationService,
    private appService: AppService,
    private syncService: SyncService,
    private modalController: ModalController,
    private loadingService: LoadingService,
    logger: LoggingService,
  ) {
    super(logger, translate);
  }

  ngOnInit() {
    super.ngOnInit();
    if (Capacitor.isNativePlatform()) {
      // JB20200924: Keyboard.setScroll is not implemented on android
      // without catch an "Unhandled Promise rejection" error is thrown.
      // Just log this behaviour, in case this will be implemented some
      // time in the future it will start working on android.
      Keyboard.setScroll({ isDisabled: true }).catch((error) => {
        this.logger.log(
          this.constructor.name,
          'Cannot disable scroll on this device',
          error?.message,
        );
      });
    }
    this.commService.requestTokenId();
    this.authSubscription =
      this.authenticationService.authenticationState.subscribe({
        next: (val) => {
          if (val === false) {
            if (this.authSubscription) {
              this.authSubscription.unsubscribe();
            }
            this.modalController.dismiss();
          }
        },
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy
    if (Capacitor.isNativePlatform()) {
      Keyboard.setScroll({ isDisabled: false }).catch((error) => {
        this.logger.log(
          this.constructor.name,
          'Cannot disable scroll on this device',
          error?.message,
        );
      });
    }
  }

  // Public Functions

  public async save(): Promise<void> {
    this.logger.log(this.constructor.name, '- Token entered:', this.token);

    try {
      await this.loadingService.showLoading();
      this.errorMessage = null;
      this.errorDetail = null;
      this.statusCode = null;

      let ra = new RemoteAction({
        authenticationRequired: true,
        cachable: false,
        fifo: false,
        endpoint: 'tokenid',
        data: {
          tokenCode: this.token,
        },
      });

      let request = await this.commService.getServerRequest(ra);

      let response = await this.commService.sendRequest(
        request.url,
        request.headers,
        request.body,
        request.httpMethod,
      );

      this.logger.log(
        this.constructor.name,
        '- Got TokenId Response:',
        response,
      );

      if (response.token) {
        await this.appService.updateTokenId(response.token);

        if (this.authSubscription) {
          this.authSubscription.unsubscribe();
        }
        this.modalController.dismiss();

        await this.loadingService.dismissLoading();
        await this.syncService.sync(true);
      }
    } catch (error) {
      if (error.status) {
        // HTML Error
        this.statusCode = error.status;

        if (error.error) {
          this.logger.error(
            this.constructor.name,
            '- HTML Error occured: ',
            error,
            error.status,
            ':',
            error.error?.message,
          );

          if (error.error?.messageCode) {
            this.errorMessage = error.error?.messageCode;
          } else {
            this.errorMessage = 'ERROR.COMMON.UNKNOWN-ERROR';
            this.errorDetail = error?.message;
          }
        } else {
          this.logger.error(
            this.constructor.name,
            '- Unknown Error occured:',
            error?.message,
          );
          this.errorMessage = 'ERROR.COMMON.UNKNOWN-ERROR';
          this.errorDetail = error?.message;
        }
      } else {
        this.logger.error(
          this.constructor.name,
          '- Internal Error occured:',
          error?.message,
        );
        this.errorMessage = 'ERROR.COMMON.INTERNAL-ERROR';
        this.errorDetail = error?.message;
      }
    } finally {
      await this.loadingService.dismissLoading();
    }
  }

  public async troubleshooting(): Promise<void> {
    this.logger.log(this.constructor.name, '- Open Troublepage');
    await Browser.open({
      url: 'https://lbslogics.atlassian.net/wiki/x/CYH-MQ',
    });
  }

  public async resend(): Promise<void> {
    this.logger.log(this.constructor.name, '- Resend Token requested');
    await this.loadingService.showLoading();
    await this.commService.requestTokenId();
    await this.loadingService.dismissLoading();
  }

  public async logout(): Promise<void> {
    this.logger.log(this.constructor.name, '- Logout requested');
    await this.authenticationService.logout();
  }

  public codeChanged() {
    this.errorMessage = undefined;
    this.errorDetail = undefined;
    this.statusCode = undefined;
  }

  // Private Functions
}
